import { AnyObject } from '@/models/GenericInterfaces'
import Message from '@/models/Message'
import { useDefaultStore } from '@/store'
import { clone } from '@/utils/tools'
import { computed, ref } from 'vue'

export function useStateManager<T>() {
  // -----------
  // -- Setup --
  // -----------
  const store = useDefaultStore()

  // ---------------
  // -- Reactives --
  // ---------------
  const history = ref<string[]>([])
  const currentIndex = ref(0)

  const latestHistoryChange = ref<number>()

  const currentState = ref<T>({} as T)

  // ---------------
  // -- Functions --
  // ---------------

  /**
   * Pushes pending state to history
   * @param changeIds not currently used. was previous used for reset keys. might be used in some form to restore table cell selection in the future
   */
  const pushHistoryEntry = (changeIds?: string[]) => {
    const maxHistory = store.settings?.app.maxBulkHistory || 25

    const stringifiedCurrentState = JSON.stringify(currentState.value)

    if (stringifiedCurrentState !== history.value[currentIndex.value]) {
      if (history.value?.length >= maxHistory) {
        history.value?.shift()
      }

      if (history.value.length > currentIndex.value + 1) {
        history.value.length = currentIndex.value + 1 // remove forward history on push
      }

      history.value.push(stringifiedCurrentState)
      currentIndex.value = history.value.length - 1
    }
  }

  const clearState = () => {
    history.value.length = 0
    currentIndex.value = 0
    currentState.value = {}
  }

  const initiateState = (state: T) => {
    clearState()
    currentState.value = state
    history.value.push(JSON.stringify(state))
  }

  /**
   * Private. Used for undo and redo
   * */
  const restoreHistory = (direction: -1 | 1) => {
    const newIndex = currentIndex.value + direction

    if (history.value[newIndex]) {
      try {
        currentIndex.value = newIndex
        currentState.value = JSON.parse(history.value[currentIndex.value])
        latestHistoryChange.value = Date.now()
      } catch (err) {
        console.log(err)
        store.addMessage(new Message('Kunde inte återställa historiken', undefined, 'danger'))
      }
    }
  }

  return {
    currentState,
    latestHistoryChange, // can be used as reset key
    undoStates: computed(() => currentIndex.value),
    redoStates: computed(() => history.value.length - 1 - currentIndex.value),
    undo: (cb?: () => void) => {
      restoreHistory(-1)
      cb?.()
    },
    redo: (cb?: () => void) => {
      restoreHistory(1)
      cb?.()
    },
    clearState,
    pushHistoryEntry,
    initiateState,
    debugHistory: history,
  }
}
